/** Eje de dismiss: abajo (bottom sheet / from-bottom) o hacia el final (panel derecho → swipe right). */
export type MilincoSheetDragAxis = 'down' | 'end';

export interface AttachMilincoSheetDragOptions {
  /** Elemento que se traslada durante el gesto (p. ej. `.cdk-overlay-pane`). */
  surface: HTMLElement;
  /** Raíz para listeners y hit-test de la zona del asa. */
  zoneRoot: HTMLElement;
  axis: MilincoSheetDragAxis;
  /** Profundidad de la zona de arrastre desde el borde asa (px). */
  zonePx?: number;
  dismissDistancePx?: number;
  dismissVelocityPxMs?: number;
  onDismiss: () => void;
  /** Selectores que no deben iniciar el gesto (botones, inputs…). */
  ignoreSelector?: string;
}

const DEFAULT_ZONE_PX = 56;
const DEFAULT_DISMISS_DISTANCE_PX = 96;
const DEFAULT_DISMISS_VELOCITY_PX_MS = 0.55;
const DEFAULT_IGNORE =
  'button, a, input, textarea, select, [mat-menu-trigger-for], [matMenuTriggerFor]';

/**
 * Drag-to-dismiss unificado (asa / cabecera).
 * Devuelve dispose para quitar listeners y limpiar transforms.
 */
export function attachMilincoSheetDrag(options: AttachMilincoSheetDragOptions): () => void {
  const zonePx = options.zonePx ?? DEFAULT_ZONE_PX;
  const dismissDistance = options.dismissDistancePx ?? DEFAULT_DISMISS_DISTANCE_PX;
  const dismissVelocity = options.dismissVelocityPxMs ?? DEFAULT_DISMISS_VELOCITY_PX_MS;
  const ignoreSelector = options.ignoreSelector ?? DEFAULT_IGNORE;
  const { surface, zoneRoot, axis, onDismiss } = options;

  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let startTs = 0;
  let delta = 0;
  let active = false;

  const resetTransform = (): void => {
    surface.style.transition = '';
    surface.style.transform = '';
  };

  const applyDelta = (offset: number): void => {
    const v = Math.max(0, offset);
    const transform =
      axis === 'down' ? `translate3d(0, ${v}px, 0)` : `translate3d(${v}px, 0, 0)`;
    surface.style.transition = 'none';
    surface.style.transform = transform;
  };

  const inDragZone = (clientX: number, clientY: number): boolean => {
    const rect = zoneRoot.getBoundingClientRect();
    if (axis === 'down') {
      return clientY - rect.top <= zonePx;
    }
    return clientX - rect.left <= zonePx;
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (event.button !== 0 && event.pointerType === 'mouse') {
      return;
    }
    if (!inDragZone(event.clientX, event.clientY)) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest(ignoreSelector)) {
      return;
    }
    active = true;
    pointerId = event.pointerId;
    startX = event.clientX;
    startY = event.clientY;
    startTs = performance.now();
    delta = 0;
    try {
      zoneRoot.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!active || event.pointerId !== pointerId) {
      return;
    }
    delta = axis === 'down' ? event.clientY - startY : event.clientX - startX;
    if (delta < 0) {
      delta = 0;
    }
    applyDelta(delta);
    if (delta > 8) {
      event.preventDefault();
    }
  };

  const finish = (event: PointerEvent): void => {
    if (!active || event.pointerId !== pointerId) {
      return;
    }
    active = false;
    pointerId = null;
    const elapsed = Math.max(1, performance.now() - startTs);
    const velocity = delta / elapsed;
    const shouldDismiss = delta >= dismissDistance || velocity >= dismissVelocity;

    if (shouldDismiss) {
      surface.style.transition = 'transform 180ms ease-out';
      surface.style.transform =
        axis === 'down' ? 'translate3d(0, 110%, 0)' : 'translate3d(110%, 0, 0)';
      window.setTimeout(() => {
        try {
          onDismiss();
        } catch {
          /* overlay already gone */
        }
        resetTransform();
      }, 160);
      return;
    }

    surface.style.transition = 'transform 200ms ease-out';
    surface.style.transform = 'translate3d(0, 0, 0)';
    window.setTimeout(resetTransform, 220);
  };

  zoneRoot.addEventListener('pointerdown', onPointerDown, { passive: true });
  zoneRoot.addEventListener('pointermove', onPointerMove, { passive: false });
  zoneRoot.addEventListener('pointerup', finish);
  zoneRoot.addEventListener('pointercancel', finish);

  return () => {
    zoneRoot.removeEventListener('pointerdown', onPointerDown);
    zoneRoot.removeEventListener('pointermove', onPointerMove);
    zoneRoot.removeEventListener('pointerup', finish);
    zoneRoot.removeEventListener('pointercancel', finish);
    resetTransform();
  };
}

/** Evalúa umbral de dismiss (tests / otros gestos). */
export function shouldDismissSheetDrag(
  deltaPx: number,
  elapsedMs: number,
  distancePx = DEFAULT_DISMISS_DISTANCE_PX,
  velocityPxMs = DEFAULT_DISMISS_VELOCITY_PX_MS,
): boolean {
  const elapsed = Math.max(1, elapsedMs);
  return deltaPx >= distancePx || deltaPx / elapsed >= velocityPxMs;
}
